import React, { useCallback, useEffect, useState } from 'react';

import api from '../../services/api';
import { useModal } from '../../hooks/modal';
import { useToast } from '../../hooks/toast';
import { CrossIcon } from '../../assets/svg/SVGComponents';
import {
  ITournamentModality,
  ITournamentSetupArena,
  ITournamentSetupData,
} from '../../pages/Tournament/CreateTournament';
import { StepTournament } from '../../pages/Tournament/CreateTournament/steps/StepTournament';
import { StepArenas } from '../../pages/Tournament/CreateTournament/steps/StepArenas';
import { StepJudges } from '../../pages/Tournament/CreateTournament/steps/StepJudges';
import { StepTeams } from '../../pages/Tournament/CreateTournament/steps/StepTeams';

import {
  Body,
  CloseButton,
  Container,
  Header,
  LoadingState,
  Title,
} from './styles';

type TManageMode = 'edit' | 'teams' | 'judges' | 'arenas';

interface ITournamentManageModalProps {
  tournamentId: string;
  mode: TManageMode;
  onUpdated?: () => Promise<void> | void;
}

interface IApiTournamentArena {
  id: string;
  tournament_modality_id?: string;
  name: string;
  is_active?: boolean;
  created_at?: string;
}

interface IApiTournamentModality {
  id: string;
  modality: string;
  max_rounds?: number;
  time_limit_seconds?: number;
  bonus_time_seconds?: number;
  bonus_points?: number;
  created_at?: string;
  arenas?: IApiTournamentArena[];
}

interface IApiTournamentResponse {
  id: string;
  name: string;
  date?: string | null;
  end_date?: string | null;
  submission_deadline?: string | null;
  max_teams_per_school?: string | null;
  status?: string;
  stage?: string;
  location?: string | null;
  school_id?: string;
  season_id?: string;
  school?: ITournamentSetupData['school'];
  modalities?: IApiTournamentModality[];
  arenas?: ITournamentSetupArena[];
}

interface IApiTournamentSetupResponse {
  school?: ITournamentSetupData['school'];
  school_id?: string;
  season_id?: string;
  arenas?: ITournamentSetupArena[];
}

const normalizeModalities = (
  modalities: IApiTournamentModality[] | undefined,
): ITournamentModality[] =>
  (modalities ?? []).map(modality => ({
    id: modality.id,
    modality: modality.modality,
    max_rounds: modality.max_rounds,
    time_limit_seconds: modality.time_limit_seconds,
    bonus_time_seconds: modality.bonus_time_seconds,
    bonus_points: modality.bonus_points,
    created_at: modality.created_at,
    arenas: modality.arenas ?? [],
  }));

const normalizeSetupArenas = (
  arenas: ITournamentSetupArena[] | undefined,
): ITournamentSetupArena[] =>
  (arenas ?? []).map(arena => ({
    name: arena.name,
    modality: arena.modality,
    categories: arena.categories ?? [],
  }));

const TournamentManageModal: React.FC<ITournamentManageModalProps> = ({
  tournamentId,
  mode,
  onUpdated,
}) => {
  const { closeModal } = useModal();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [tournamentData, setTournamentData] =
    useState<ITournamentSetupData | null>(null);

  const handleUpdated = useCallback(async () => {
    if (onUpdated) {
      await onUpdated();
    }
  }, [onUpdated]);

  const loadTournamentData = useCallback(async () => {
    try {
      setIsLoading(true);

      const [tournamentResult, setupResult] = await Promise.allSettled([
        api.get<IApiTournamentResponse>(`/tournaments/${tournamentId}`),
        api.get<IApiTournamentSetupResponse>('/tournaments/setup/frl-2026'),
      ]);

      if (tournamentResult.status !== 'fulfilled') {
        throw new Error('failed_tournament_fetch');
      }

      const tournamentResponse = tournamentResult.value.data;
      const setupResponse =
        setupResult.status === 'fulfilled' ? setupResult.value.data : undefined;

      const modalities = normalizeModalities(tournamentResponse.modalities);
      const setupArenas = normalizeSetupArenas(
        tournamentResponse.arenas ?? setupResponse?.arenas,
      );
      const fallbackArenas: ITournamentSetupArena[] = modalities.flatMap(
        modality =>
          modality.arenas.map(arena => ({
            name: arena.name,
            modality: modality.modality,
            categories: [],
          })),
      );

      setTournamentData({
        id: tournamentResponse.id,
        name: tournamentResponse.name,
        date: tournamentResponse.date ?? null,
        end_date: tournamentResponse.end_date ?? null,
        submission_deadline: tournamentResponse.submission_deadline ?? null,
        max_teams_per_school: tournamentResponse.max_teams_per_school ?? null,
        status: tournamentResponse.status ?? '',
        stage: tournamentResponse.stage ?? '',
        location: tournamentResponse.location ?? null,
        school: tournamentResponse.school ?? setupResponse?.school,
        school_id:
          tournamentResponse.school_id ??
          setupResponse?.school?.id ??
          setupResponse?.school_id ??
          '',
        season_id:
          tournamentResponse.season_id ?? setupResponse?.season_id ?? '',
        arenas: setupArenas.length ? setupArenas : fallbackArenas,
        modalities,
      });
    } catch {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description:
          'Não foi possível carregar os dados do torneio para edição.',
      });
      closeModal();
    } finally {
      setIsLoading(false);
    }
  }, [addToast, closeModal, tournamentId]);

  useEffect(() => {
    loadTournamentData();
  }, [loadTournamentData]);

  const modalTitle =
    mode === 'edit'
      ? 'Editar torneio'
      : mode === 'teams'
        ? 'Adicionar equipe'
        : mode === 'arenas'
          ? 'Editar arenas'
          : 'Adicionar juiz';

  const renderBody = () => {
    if (isLoading) {
      return <LoadingState>Carregando dados do torneio...</LoadingState>;
    }

    if (!tournamentData) {
      return <LoadingState>Não foi possível carregar os dados.</LoadingState>;
    }

    if (mode === 'edit') {
      return (
        <StepTournament
          data={tournamentData}
          onCancel={closeModal}
          onSave={updatedTournamentData => {
            setTournamentData(updatedTournamentData);
            closeModal();
            handleUpdated();
          }}
        />
      );
    }

    if (mode === 'arenas') {
      return (
        <StepArenas
          tournamentData={tournamentData}
          onCancel={closeModal}
          onSave={updatedTournamentData => {
            setTournamentData(updatedTournamentData);
            closeModal();
            handleUpdated();
          }}
        />
      );
    }

    if (mode === 'teams') {
      return (
        <StepTeams
          tournamentData={tournamentData}
          onSaveSuccess={handleUpdated}
        />
      );
    }

    return (
      <StepJudges
        tournamentData={tournamentData}
        onCancel={closeModal}
        onSave={async updatedTournamentData => {
          setTournamentData(updatedTournamentData);
          await handleUpdated();
          closeModal();
        }}
      />
    );
  };

  return (
    <Container>
      <Header>
        <Title>{modalTitle}</Title>
        <CloseButton
          type="button"
          onClick={closeModal}
          aria-label="Fechar modal"
        >
          <CrossIcon width="0.9rem" stroke="currentColor" strokeWidth={2} />
        </CloseButton>
      </Header>

      <Body>{renderBody()}</Body>
    </Container>
  );
};

export default TournamentManageModal;
